import React, { useState, useRef, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button, TextField, Box, Typography, Snackbar, Alert, Slide } from '@mui/material';
import { AuthProvider } from "./context/AuthContext"; // AuthContext importieren
import { useAuth } from "./hooks/useAuth"; // 👈 Auth-Status abrufen
import PullToRefresh from 'react-pull-to-refresh';
import "./config/axiosConfig";
import { DialogProvider } from './components/Modal/DialogContext';
// Import der Komponenten
import TemplatesPage from './pages/Templates'; // Import der neuen Seite
import Joyride from 'react-joyride';
import { Step } from 'react-joyride';
import Header from './Header'; // .tsx hinzugen
import Session from './Session';
import SessionGenerators from './Sessiongenerators';
import Documentation from './Documentation';
import SettingsLayout from './pages/settings/SettingsLayout';
import Tooltip from './components/Tooltip';

//import ChangePassword from './pages/settings/Password';
import PrivateRoute from './components/PrivateRoute';
import PublicRoute from './components/PublicRoute';
// Import der Seiten
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import RegisterBetaPage from './pages/RegisterBetaPage';

import RequestPasswordReset from './pages/RequestPasswordReset';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';



import NotFound from './pages/NotFound';
import ManageProfile from './pages/settings/Profile';
import ChangePassword from './pages/settings/ChangePassword';
import ClientConsent from './pages/settings/ClientConsent';
import DocumentationSettings from './pages/settings/DocumentationSettings';
import PlanPicker from './pages/settings/PlanPicker';

import DevTools from './DevTools';
import AdminApp from './admin/AdminApp';

import { useSessionStore, } from "./SessionStore"; // Zustand-Store für die aktuelle Sitzung

import { SiteSettingsProvider, useSiteSettings } from './context/SiteSettingsContext'; // Import des SiteSettings-Providers
import { UserProvider } from './context/UserContext';

// CSS-Datei importieren
import './css/App.css';
import './css/Header.css';
import './css/index-00a.css';
import './css/index-00b.css';
import './css/index-00c.css';
import './css/index-00d.css';
import './css/index-00e.css';
import './css/usePatientSessions.css';
import './css/Tabs.css';
import './css/Menu.css';
import './css/date-353c96b3.css';
import './css/Login-71bcb644.css';
import './css/InputField-6ca11b45.css';
import './css/AuthLayout-db10eaf1.css';
import './css/cosmeticsUtils-34de0545.css';

import './css/paymentSubscription-98110470.css';
//Beginn der App //

const queryClient = new QueryClient();

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <UserProvider>
          <DialogProvider>
            <SiteSettingsProvider>
              <Router>
                <HistoryHandler />
                <MainApp />
              </Router>
            </SiteSettingsProvider>
          </DialogProvider>
        </UserProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

function HistoryHandler() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handlePopState = () => {
      // Hier kannst du eigene Logik einbauen
      // Beispiel: Dialog schließen statt zurück navigieren
      console.log('Zurück-Button erkannt, aktuelle URL:', location.pathname);
      // navigate("/andere-route"); // Falls du umleiten willst
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [location]);

  return null;
}




const MainApp: React.FC = () => {
  const { user } = useAuth(); // ✅ user abrufen
  const navigate = useNavigate();
  const { settings, loading } = useSiteSettings();
  const [runTour, setRunTour] = useState(false);
  const [steps, setSteps] = useState<Step[]>([]);
  const [joyrideKey, setJoyrideKey] = useState(0);
  //const [stepsReady, setStepsReady] = useState(false);




  //const closeLoadingDialog = () => setIsLoading(false);

  //Snackbar
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "warning" | "error">("success");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const sessionData = useSessionStore((state) => state.sessionData);
  const isAudioDialogOpen = useSessionStore((state) => state.isAudioDialogOpen);

  //Sitzung
  const { isSessionSelected, setIsSessionSelected } = useSessionStore(); // ⬅ Jetzt aus dem globalen Store

  //Entwicklung
  const [showDevTools, setShowDevTools] = useState(false);
  const toggleView = () => {
    setShowDevTools((prev) => !prev);
  };

  useEffect(() => {
    console.log("📡 sessionData wurde aktualisiert:", sessionData?.id);
    if (sessionData?.id! > 0) {
      setIsSessionSelected(true)
    }
  }, [sessionData]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      useSessionStore.getState().reset();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const startTour = () => {
    const sessionEl = document.querySelector('#session-generator') as HTMLElement | null;
    const configureEl_1 = document.querySelector('#note-configure-name-1') as HTMLElement | null;
    const configureEl_2 = document.querySelector('#note-configure-name-2') as HTMLElement | null;
    const noteEl = document.querySelector('#note-documentation') as HTMLElement | null;




    if (sessionEl && sessionEl.offsetParent !== null) {
      setRunTour(false); // zuerst stoppen
      setSteps([
        {
          target: '#session-generator',
          content: (
            <div style={{
              textAlign: 'left',
              maxWidth: '500px',
              backgroundColor: "transparent",
              boxShadow: 'none',
              pointerEvents: 'none'
            }}>
              <p><strong>Hier ist Ihre Zentrale für die Sitzungsaufnahme.</strong></p>
              <p>Sie können eine neue Sitzung starten, indem Sie:</p>
              <ul style={{ listStyleType: 'disc', paddingLeft: '20px', marginTop: '8px', marginBottom: '10px' }}>
                <li style={{ backgroundColor: "transparent" }}>eine Live-<strong>Aufnahme</strong> mit Ihren Klient:innen durchführen,</li>
                <li style={{ backgroundColor: "transparent" }}>ein <strong>Diktat</strong> einsprechen, um den Sitzungsverlauf zu berichten,</li>
                <li style={{ backgroundColor: "transparent" }}>oder eine vorhandene Audiodatei <strong>hochladen.</strong></li>
              </ul>
              <p>Nutzen Sie die am unteren Rand verlinkten <strong>Tipps</strong>, um das Beste aus jeder Methode herauszuholen.</p>
            </div>
          ),
          disableBeacon: true,
          placement: 'bottom',
        }, {
          target: '#session-cards',
          content: (
            <div style={{
              textAlign: 'left',
              maxWidth: '500px',
              backgroundColor: "transparent",
              boxShadow: 'none',
              pointerEvents: 'none'
            }}>
              <p><strong>Ihre gespeicherten Sitzungen</strong></p>
              <p>Wählen Sie eine Karte aus, um eine Dokumentation zu erstellen oder zu bearbeiten:</p>
              <ul style={{ listStyleType: 'disc', paddingLeft: '20px', marginTop: '6px', marginBottom: '8px' }}>
                <li style={{ backgroundColor: "transparent" }}><strong>Neue Sitzung:</strong> Es wurde noch keine Dokumentation generiert.</li>
                <li style={{ backgroundColor: "transparent" }}><strong>Erfasst:</strong> Eine erste Dokumentation wurde erstellt und kann weiter bearbeitet werden.</li>
                <li style={{ backgroundColor: "transparent" }}><strong>Gespeichert:</strong> Die finale Dokumentation steht noch zum Download bereit.</li>
              </ul>
              <p>Sitzungen können jederzeit über das Menü gelöscht werden – sämtliche Daten werden dabei vollständig entfernt.</p>
            </div>
          ),
          disableBeacon: true,
          placement: 'right',
        },
        {
          target: '#new-session-button',
          content: (
            <div style={{
              textAlign: 'left',
              maxWidth: '500px',
              backgroundColor: "transparent",
              boxShadow: 'none',
              pointerEvents: 'none'
            }}>
              <p><strong>Direkter Zugang zur Aufnahme</strong></p>
              <p>Starten Sie Ihre nächste Sitzung schnell und unkompliziert.</p>

            </div>
          ),
          disableBeacon: true,
          placement: 'bottom',
        },
      ]);
      setTimeout(() => {
        setRunTour(true); // leicht verzögert neu starten
      }, 50);
    } else if (configureEl_1 && configureEl_1.offsetParent !== null) {
      setRunTour(false); // zuerst stoppen
      setSteps([
        {
          target: '#note-configure-name-1',
          content: (
            <div style={{
              textAlign: 'left',
              maxWidth: '500px',
              backgroundColor: "transparent",
              boxShadow: 'none',
              pointerEvents: 'none'
            }}>
              <p><strong>Titel der Sitzung</strong></p>
              <p>Vergeben Sie einen passenden Titel für die Sitzung – möglichst anonymisiert. Nach dem Klick auf <strong>„Speichern &amp; Weiter“</strong> können Sie weitere Einstellungen für die KI-Dokumentation vornehmen.</p>

            </div>
          ),
          disableBeacon: true,
          placement: 'bottom',
        },
      ]);
      setTimeout(() => {
        setRunTour(true); // leicht verzögert neu starten
      }, 50);
    }
    else if (configureEl_2 && configureEl_2.offsetParent !== null) {
      setRunTour(false); // zuerst stoppen
      setSteps([
        {
          target: '#note-configure-name-2',
          content: (
            <div style={{
              textAlign: 'left',
              maxWidth: '500px',
              backgroundColor: "transparent",
              boxShadow: 'none',
              pointerEvents: 'none'
            }}>
              <p><strong>Titel der Sitzung</strong></p>
              <p>Vergeben Sie einen passenden Titel für die Sitzung – möglichst anonymisiert.</p>

            </div>
          ),
          disableBeacon: true,
          placement: 'bottom',
        }, {
          target: '#note-configure-date',
          content: (
            <div style={{
              textAlign: 'left',
              maxWidth: '500px',
              backgroundColor: "transparent",
              boxShadow: 'none',
              pointerEvents: 'none'
            }}>
              <p><strong>Datum</strong></p>
              <p>Standardmäßig wird das Erstellungsdatum übernommen. Falls die Sitzung früher stattgefunden hat (z. B. bei Diktat oder Upload), passen Sie es einfach an.</p>

            </div>
          ),
          disableBeacon: true,
          placement: 'bottom',
        }, {
          target: '#note-configure-pariticipant',
          content: (
            <div style={{
              textAlign: 'left',
              maxWidth: '500px',
              backgroundColor: "transparent",
              boxShadow: 'none',
              pointerEvents: 'none'
            }}>
              <p><strong>Teilnehmer</strong></p>
              <p>Die KI erkennt, ob es sich um Einzel-, Paar- oder Gruppentherapie handelt. Beachten Sie, dass Gruppensitzungen nur nachträglich per Diktat verarbeitet werden können. Bitte geben Sie in diesem Fall die Anzahl der Teilnehmenden im Diktat an.</p>

            </div>
          ),
          disableBeacon: true,
          placement: 'bottom',
        }, {
          target: '#note-configure-gender',
          content: (
            <div style={{
              textAlign: 'left',
              maxWidth: '500px',
              backgroundColor: "transparent",
              boxShadow: 'none',
              pointerEvents: 'none'
            }}>
              <p><strong>Geschlecht</strong></p>
              <p>Wählen Sie hier aus, ob es sich um einen Mann oder eine Frau handelt (bei Einzeltherapie). Ihre bevorzugte Anredeform – etwa „Klient(in)“ oder „Patient(in)“ – können Sie in den <a href="/settings/documentationsettings" target="_blank" style={{ textDecoration: 'underline' }}>Voreinstellungen</a> festlegen.</p>

            </div>
          ),
          disableBeacon: true,
          placement: 'bottom',
        }, {
          target: '#note-configure-type',
          content: (
            <div style={{
              textAlign: 'left',
              maxWidth: '500px',
              backgroundColor: "transparent",
              boxShadow: 'none',
              pointerEvents: 'none'
            }}>
              <p><strong>Dokumentations-Typ</strong></p>
              <p>Wählen Sie aus, welche Art der Dokumentation Sie für diese Sitzung benötigen. Anschließend stehen Ihnen passende Vorlagen (Templates) zur Auswahl.</p>

            </div>
          ),
          disableBeacon: true,
          placement: 'bottom',
        }, {
          target: '#note-configure-templates',
          content: (
            <div style={{
              textAlign: 'left',
              maxWidth: '500px',
              backgroundColor: "transparent",
              boxShadow: 'none',
              pointerEvents: 'none'
            }}>
              <p><strong>Templates</strong></p>
              <p>Ihnen steht mindestens eine LumaNote-Vorlage zur Verfügung. In einem zukünftigen Update können Sie auch eigene, individuelle Templates erstellen.</p>
              <p>Klicken Sie anschließend auf <strong>„Generiere Dokumentation“</strong>, um die Erstellung zu starten.</p>
            </div>
          ),
          disableBeacon: true,
          placement: 'bottom',
        },
      ]);
      setTimeout(() => {
        setRunTour(true); // leicht verzögert neu starten
      }, 50);
    } else if (noteEl && noteEl.offsetParent !== null) {
      setRunTour(false); // zuerst stoppen
      setSteps([
        {
          target: '#note-documentation-buttons',
          content: (
            <div style={{
              textAlign: 'left',
              maxWidth: '500px',
              backgroundColor: "transparent",
              boxShadow: 'none',
              pointerEvents: 'none'
            }}>
              <p><strong>Funktionen</strong></p>
              <p>Diese Funktionen verbergen sich hinter den Buttons:</p>

              <ul style={{ listStyleType: 'disc', paddingLeft: '20px', marginTop: '8px', marginBottom: '10px' }}>
                <li style={{ backgroundColor: "transparent" }}><strong>Details:</strong> Blenden Sie die Sitzungsdetails aus.</li>
                <li style={{ backgroundColor: "transparent" }}><strong>Download:</strong> Speichern Sie die fertige Dokumentation.</li>
                <li style={{ backgroundColor: "transparent" }}><strong>Kopieren:</strong> Übernehmen Sie den Text in den Zwischenspeicher.</li>
                <li style={{ backgroundColor: "transparent" }}><strong>Neu erstellen:</strong> Generieren Sie eine alternative Version mit der KI.</li>
              </ul>

              <p>Die Wolke zeigt an, ob Ihre Änderungen gespeichert wurden.</p>

            </div>
          ),
          disableBeacon: true,
          placement: 'auto',

        }, {
          target: '#note-documentation-quill',
          content: (
            <div style={{
              textAlign: 'left',
              maxWidth: '500px',
              backgroundColor: "transparent",
              boxShadow: 'none',
              pointerEvents: 'none'
            }}>
              <p><strong>Ihre Dokumentation</strong></p>
              <p>Im Texteditor können Sie Anpassungen vornehmen. Diese werden regelmäßig automatisch gespeichert.</p>




            </div>
          ),
          disableBeacon: true,
          placement: 'auto',
        },
      ]);
      setTimeout(() => {
        setRunTour(true); // leicht verzögert neu starten
      }, 50);
    }
    else {
      console.warn("Element nicht sichtbar: #session-generator");
    }
  };

  // Removed the useEffect with setInterval that delayed setting steps and stepsReady


  const styles: { [key: string]: React.CSSProperties } = {
    header: {
      backgroundColor: '#2A5941',
      color: '#FFFFFF',
      padding: '20px',
      textAlign: 'center',
      borderRadius: '10px',
    },
    container: {
      backgroundColor: '#F1F8E9',
      padding: '20px',
      minHeight: '100vh',
    },
    button: {
      backgroundColor: '#C5E1A5',
      color: '#2A5941',
      margin: '10px',
      border: '1px solid #2A5941',
      borderRadius: '8px',
      padding: '10px 20px',
    },
    textField: {
      border: '1px solid #C5E1A5',
      borderRadius: '5px',
      marginTop: '10px',
      backgroundColor: '#FFFFFF',

    },

    dialogContent: {
      padding: '20px',
    },
  };



  ///////////////////////////////////
  // Anfang des HTML-Layouts ////////
  ///////////////////////////////////

  return (
    <>
      <Joyride
        key={joyrideKey}
        steps={steps}
        run={runTour}
        showProgress
        showSkipButton
        continuous
        scrollToFirstStep={false}
        disableScrolling={true}
        spotlightClicks={true}
        disableOverlayClose
        disableOverlay={false}
        locale={{
          back: 'Zurück',
          close: 'Schließen',
          last: 'Fertig',
          next: 'Weiter',
          skip: 'Überspringen',
          nextLabelWithProgress: 'Weiter (Schritt {step} von {steps})'


        }}
        styles={{
          options: {
            width: 'auto',
            backgroundColor: '#ffffff',
            zIndex: 9999,
            primaryColor: '#32768d',
          },
          tooltipContainer: {
            width: 'auto',
            maxWidth: '500px',
            minWidth: '100px',
            padding: '4px',
            paddingBottom: '0px',
            fontSize: '0.85rem',
            transition: 'all 0.4s ease-in-out',
          },
          spotlight: {
            transition: 'all 0.4s ease-in-out',
          }
        }}
        callback={(data) => {
          const { status, action } = data;
          if (['finished', 'skipped'].includes(status) || action === 'close') {
            setRunTour(false);
            setJoyrideKey(prev => prev + 1);
          }
        }}
      />
      <Routes>

        <Route path="/" element={
          <PrivateRoute>

            <div className="c-wrapper-custom">

              <Header showDevTools={showDevTools} toggleView={toggleView} />
              <div className="c-body-custom">
                <div className="c-main-custom">
                  <div className="custom-container disableScrolling ">

                    <div className="tw-mt-2 lg:tw-mt-4 tw-flex tw-items-center tw-justify-end z-9999">
                      <Tooltip text="Kurze Hilfe – abgestimmt auf das, was Sie gerade machen.">
                        <button onClick={() => { startTour(); }} type="button" className="help-icon tw-flex tw-items-center tw-space-x-2 tw-text-primary-500 tw-text-md">
                          <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 9.45308V9.20424C8 8.39832 8.50606 7.97163 9.0121 7.62793C9.50608 7.29607 10 6.86942 10 6.0872C10 4.99683 9.10845 4.11975 8 4.11975C6.89155 4.11975 6 4.99683 6 6.0872" stroke="#32768d" stroke-linecap="round" stroke-linejoin="round"></path>
                            <path d="M7.99414 12.4531H8.00614" stroke="#32768d" stroke-linecap="round" stroke-linejoin="round"></path><path d="M8.00016 14.7865C11.6668 14.7865 14.6668 11.7865 14.6668 8.11979C14.6668 4.45312 11.6668 1.45312 8.00016 1.45312C4.3335 1.45312 1.3335 4.45312 1.3335 8.11979C1.3335 11.7865 4.3335 14.7865 8.00016 14.7865Z" stroke="#32768d" stroke-linecap="round" stroke-linejoin="round"></path>
                          </svg>
                          <span>Hilfe
                          </span>
                        </button></Tooltip>
                    </div>




                    <div className="tw-pt-2 lg:tw-pt-4 tw-pb-20">
                      <div className="tw-relative">
                        <div className="dashboard-container tw-pb-10" style={{ display: 'block' }}>
                          <div className="tw-flex tw-flex-col lg:tw-flex-row tw-justify-between">
                            <Session
                              setSnackbarMessage={setSnackbarMessage}
                              setSnackbarSeverity={setSnackbarSeverity}
                              setOpenSnackbar={setOpenSnackbar}
                            />

                            {!isSessionSelected && (
                              <div className="lg:w-8/12 order-1 lg:order-2 mb-8  lg:mb-0">
                                <SessionGenerators />
                              </div>
                            )}

                            {isSessionSelected && (
                              <div className="lg:w-9/12 order-1 lg:order-2 mb-8  lg:mb-0" >
                                <div className="lg:ml-10  ml-0">
                                  {showDevTools ? <DevTools /> : <Documentation />}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <Snackbar
                      open={openSnackbar}
                      autoHideDuration={3500}
                      onClose={() => setOpenSnackbar(false)}
                      anchorOrigin={{ vertical: "top", horizontal: "center" }}
                      TransitionComponent={Slide}
                    >
                      <Alert
                        onClose={() => setOpenSnackbar(false)}
                        severity={snackbarSeverity}
                        sx={{
                          backgroundColor:
                            snackbarSeverity === "success"
                              ? "rgba(76, 175, 80, 0.8)"
                              : snackbarSeverity === "warning"
                                ? "rgba(255, 193, 7, 0.8)"
                                : "rgba(244, 67, 54, 0.8)",
                          color: "#fff",
                          borderRadius: "10px",
                          fontSize: "1.1rem",
                        }}
                      >
                        {snackbarMessage}
                      </Alert>
                    </Snackbar>
                  </div></div></div></div>

          </PrivateRoute>
        } />
        <Route path="/templates" element={<PrivateRoute>
          <div className="c-wrapper-custom">

            <Header showDevTools={showDevTools} toggleView={toggleView} />
            <div className="c-body-custom">
              <div className="c-main-custom">
                <div className="custom-container disableScrolling">
                  <div className="tw-mt-2 lg:tw-mt-4 tw-flex tw-items-center tw-justify-end"><button type="button" className="help-icon tw-flex tw-items-center tw-space-x-2 tw-text-primary-500 tw-text-xs"><svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 9.45308V9.20424C8 8.39832 8.50606 7.97163 9.0121 7.62793C9.50608 7.29607 10 6.86942 10 6.0872C10 4.99683 9.10845 4.11975 8 4.11975C6.89155 4.11975 6 4.99683 6 6.0872" stroke="#32768d" stroke-linecap="round" stroke-linejoin="round"></path><path d="M7.99414 12.4531H8.00614" stroke="#32768d" stroke-linecap="round" stroke-linejoin="round"></path><path d="M8.00016 14.7865C11.6668 14.7865 14.6668 11.7865 14.6668 8.11979C14.6668 4.45312 11.6668 1.45312 8.00016 1.45312C4.3335 1.45312 1.3335 4.45312 1.3335 8.11979C1.3335 11.7865 4.3335 14.7865 8.00016 14.7865Z" stroke="#32768d" stroke-linecap="round" stroke-linejoin="round"></path></svg><span>Hilfe</span></button></div>

                  <TemplatesPage /></div></div></div></div></PrivateRoute>} />
        <Route path="/settings" element={
          <PrivateRoute>
            <div className="c-wrapper-custom">

              <Header showDevTools={showDevTools} toggleView={toggleView} />
              <div className="c-body-custom">
                <div className="c-main-custom">
                  <div className="custom-container disableScrolling">
                    <SettingsLayout />
                  </div></div></div></div>
          </PrivateRoute>
        }>
          <Route index element={<ManageProfile />} />
          <Route path="changepassword" element={<ChangePassword />} />
          <Route path="clientconsent" element={<ClientConsent />} />
          <Route path="documentationsettings" element={<DocumentationSettings />} />
          <Route path="planpicker" element={<PlanPicker />} />

        </Route>
        <Route path="/frontend-admin-1107/*" element={

          <AdminApp />

        } />
        <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
        <Route path="/register-beta" element={<PublicRoute><RegisterBetaPage /></PublicRoute>} />

        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/request-password-reset" element={<PublicRoute><RequestPasswordReset /></PublicRoute>} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        v
        <Route path="*" element={<NotFound />} />

      </Routes>
    </>
  );
}

export default App;
